import { useSyncExternalStore } from 'react';

// Module-level store, deliberately not sessionStorage: the admin SPA never reloads between
// the Web Studio list route and the editor route -- it's a client-side history.push both ways,
// so plain module state survives the transition.

// Must stay in sync with `Container` in ./container.tsx.
export const NAV_EXPANDED_WIDTH = 200;

// Matches Puck's plugin-bar rail width (`--puck-pluginbar-width: 68px` in
// `@puckeditor/core@0.23.0`'s dist/index.css, scoped to `@media (min-width: 638px)`; below that
// Puck reflows to a row layout with no left rail).
//
// Total footprint, not solid fill: pixel analysis (1440x900 dpr 1) shows 67px of `#003d29`
// green fill then a 1px `rgb(225,230,235)` border before the canvas starts, same construction
// as the admin sidenav's rail. The 67/1 split is encoded in cms-editor-frame.tsx, not here.
export const NAV_COLLAPSED_WIDTH = 68;

export const NAV_FADE_OUT_MS = 80;
export const NAV_FADE_IN_MS = 140;
export const NAV_COLLAPSE_MS = 320;
export const NAV_EXPAND_MS = 220;

// Ghost-logo fade-in duration once the collapse slide finishes -- fast enough not to feel
// laggy after a slide that already took 320ms.
export const NAV_LOGO_FADE_IN_MS = 200;

// Settle beat after `cms:rendered` arrives before fading the loading overlay -- avoids a
// jarring instant swap the moment the message lands.
export const NAV_RENDERED_SETTLE_MS = 150;

// Ceiling for the case `cms:rendered` never arrives (mismatched consumer build, or an
// error path that never mounts <Puck>) -- guarantees the overlay comes down eventually rather
// than stranding the user. Counted from `ready && slideDone`, not mount. Generous on purpose:
// better to wait a few extra seconds than to ever hang.
export const NAV_RENDERED_FALLBACK_MS = 4000;

// Recovery guard, not a visual timing: if the expected history.push never remounts (route
// guard no-ops, pushed URL === current), the sidebar would otherwise be stranded at opacity: 0
// forever. AdminSidebar arms this timer on fade-out; still mounted when it fires means
// navigation didn't happen, so it self-heals. Comfortably longer than NAV_FADE_OUT_MS.
export const NAV_COLLAPSE_ABORT_MS = 1500;

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

type State = {
  // True while the wide-to-narrow (or narrow-to-wide) rail transition is in flight.
  collapsing: boolean;
  // Consume-once: only true when a click armed the enter animation, so a deep link/refresh
  // into the editor route doesn't play it.
  armed: boolean;
};

let state: State = { collapsing: false, armed: false };
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): boolean {
  return state.collapsing;
}

export function useNavCollapsing(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot);
}

export function beginNavCollapse(): Promise<void> {
  state = { collapsing: true, armed: true };
  notify();

  if (prefersReducedMotion()) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    setTimeout(resolve, NAV_FADE_OUT_MS);
  });
}

export function consumeArmedCollapse(): boolean {
  const { armed } = state;
  state = { ...state, armed: false };
  return armed;
}

// Plain "navigation ended" event for non-React consumers (e.g. open-cms-editor.ts) that need
// to reset their own in-flight guards when a collapse ends, without subscribing to every
// collapsing update via useSyncExternalStore.
const navCollapseEndedListeners = new Set<() => void>();

export function subscribeNavCollapseEnded(listener: () => void): () => void {
  navCollapseEndedListeners.add(listener);
  return () => navCollapseEndedListeners.delete(listener);
}

export function endNavCollapse(): void {
  state = { collapsing: false, armed: false };
  notify();
  navCollapseEndedListeners.forEach((listener) => listener());
}

// Test-only: resets the module store between tests. Not for production use.
// eslint-disable-next-line @typescript-eslint/naming-convention
export function __resetNavChromeTransition(): void {
  state = { collapsing: false, armed: false };
  listeners.clear();
}
